import { apiRequest } from "../services/apiRequest"

/**
 * Chatbot Checker admin API — wired to the live backend.
 *
 *   GET   /api/chatbot/stats/
 *   GET   /api/chatbot/sessions/?rating=&search=&page=
 *   GET   /api/chatbot/sessions/<id>/
 *   PATCH /api/chatbot/sessions/<id>/            { "quality": "good" | "bad" }
 *   PATCH /api/chatbot/messages/<id>/flag/       { "flagged": true }
 *
 * Auth: the axios instance (services/axios.js) auto-attaches the Bearer token.
 *
 * The backend response field names were not specified, so every method
 * normalizes the payload defensively (tries the common variants) into the stable
 * shapes the ChatbotChecker page consumes. If a field ends up blank in the UI,
 * add the real backend key to the matching `pick(...)` list below — that's the
 * only change needed.
 */

const BASE = "/api/chatbot"

/** First defined (non-null) value among the given object keys. */
const pick = (obj, ...keys) => {
  if (!obj) return undefined
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k]
  }
  return undefined
}

const toNumber = (v, fallback = 0) => {
  const n = typeof v === "string" ? parseFloat(v) : Number(v)
  return Number.isFinite(n) ? n : fallback
}

/** Normalize any backend quality/rating value to "good" | "bad" | "unrated". */
const normalizeRating = (value) => {
  const v = String(value ?? "").toLowerCase().trim()
  if (["good", "positive", "up", "1", "true"].includes(v)) return "good"
  if (["bad", "negative", "down", "0"].includes(v)) return "bad"
  return "unrated"
}

/** Pull the list array + total count out of a paginated (or bare-array) body. */
const extractList = (body) => {
  const results =
    pick(body, "results", "sessions", "conversations", "data", "items") ||
    (Array.isArray(body) ? body : [])
  const count = pick(body, "count", "total", "total_count")
  return {
    results: Array.isArray(results) ? results : [],
    count: count != null ? toNumber(count) : (Array.isArray(results) ? results.length : 0),
  }
}

const normalizeSession = (row = {}) => {
  const messages = pick(row, "messages", "history")
  return {
    id: pick(row, "id", "session_id", "uuid", "pk"),
    user_name:
      pick(row, "user_name", "name", "customer_name", "username") ||
      pick(row.user || row.customer || {}, "name", "full_name") ||
      "",
    user_email:
      pick(row, "user_email", "email") ||
      pick(row.user || row.customer || {}, "email") ||
      "",
    rating: normalizeRating(pick(row, "quality", "rating", "sentiment")),
    status: pick(row, "status", "state") || "",
    started_at: pick(row, "started_at", "created_at", "created", "timestamp", "date"),
    message_count: toNumber(
      pick(row, "message_count", "messages_count", "num_messages") ??
        (Array.isArray(messages) ? messages.length : 0),
    ),
    avg_response_ms: toNumber(
      pick(row, "avg_response_ms", "average_response_ms", "avg_response_time_ms"),
    ),
    preview:
      pick(row, "preview", "first_message", "last_message", "snippet", "summary") ||
      "",
  }
}

const normalizeMessage = (m = {}, idx = 0) => {
  const senderRaw = String(
    pick(m, "from", "sender", "role", "author") ?? "",
  ).toLowerCase()
  const isBot =
    m.is_bot === true ||
    m.from_bot === true ||
    ["bot", "assistant", "ai", "system", "agent"].includes(senderRaw)
  return {
    id: pick(m, "id", "message_id", "pk") ?? `msg-${idx}`,
    from: isBot ? "bot" : "user",
    text: pick(m, "text", "message", "content", "body") || "",
    imageUrl: pick(m, "image_url", "imageUrl") || null,
    timestamp: pick(m, "timestamp", "created_at", "created", "date"),
    response_ms: toNumber(
      pick(m, "response_ms", "latency_ms", "response_time_ms", "response_time"),
      null,
    ),
    flagged: pick(m, "flagged", "is_flagged") === true,
  }
}

/**
 * Does the search text look like a session id (numeric pk or UUID)?
 * Used to route the query to the direct /sessions/<id>/ lookup, since the
 * backend's `search` only covers name / email / message text.
 */
const looksLikeSessionId = (value) => {
  const v = String(value ?? "").trim()
  if (!v) return false
  if (/^\d+$/.test(v)) return true
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v)
}

export const ChatbotApi = {
  /** GET /api/chatbot/stats/ → normalized overview for the 4 summary cards. */
  getOverview: async () => {
    const res = await apiRequest("get", `${BASE}/stats/`)
    const body = res?.data?.result ?? res?.data ?? {}

    const positive = toNumber(pick(body, "positive_count", "good_count", "good"))
    const negative = toNumber(
      pick(body, "negative_count", "bad_count", "bad", "flagged_count", "flagged"),
    )
    const unrated = toNumber(pick(body, "unrated_count", "unrated"))
    const rated = positive + negative

    const result = {
      total_conversations: toNumber(
        pick(body, "total_conversations", "total_sessions", "sessions", "total"),
      ),
      total_messages: toNumber(pick(body, "total_messages", "messages")),
      positive_count: positive,
      negative_count: negative,
      unrated_count: unrated,
      positive_rate: toNumber(
        pick(body, "positive_rate") ??
          (rated ? Math.round((positive / rated) * 100) : 0),
      ),
      avg_response_ms: toNumber(pick(body, "avg_response_ms", "average_response_ms")),
      avg_messages_per_conversation: toNumber(
        pick(body, "avg_messages_per_conversation", "avg_messages"),
      ),
    }
    return { data: { success: true, result } }
  },

  /** GET /api/chatbot/sessions/?rating=&search=&page= → normalized table rows. */
  getConversations: async (query = {}) => {
    const searchTerm = String(query.search ?? "").trim()

    // Searching by chat id: hit the detail endpoint directly and return it as a
    // single row. Falls through to the normal search when there's no such id.
    if (looksLikeSessionId(searchTerm)) {
      try {
        const res = await apiRequest(
          "get",
          `${BASE}/sessions/${searchTerm}/`,
          undefined,
          { skipCache: true },
        )
        const body = res?.data?.result ?? res?.data ?? {}
        const rawSession = pick(body, "session", "conversation") || body
        const rawMessages =
          pick(body, "messages", "history") ||
          pick(rawSession, "messages", "history") ||
          []
        const row = normalizeSession({
          ...rawSession,
          messages: Array.isArray(rawMessages) ? rawMessages : [],
        })
        if (row.id != null) {
          if (!row.preview) {
            const first = (Array.isArray(rawMessages) ? rawMessages : []).map(
              normalizeMessage,
            )[0]
            row.preview = first?.text || ""
          }
          const matchesRating =
            !query.rating ||
            query.rating === "all" ||
            row.rating === query.rating
          return {
            data: {
              success: true,
              result: matchesRating
                ? { count: 1, results: [row] }
                : { count: 0, results: [] },
            },
          }
        }
      } catch (e) {
        // Not an existing id (404) — fall back to the regular search below.
      }
    }

    const params = new URLSearchParams()
    if (query.rating && query.rating !== "all")
      params.set("rating", String(query.rating))
    if (query.search && String(query.search).trim())
      params.set("search", String(query.search).trim())
    if (query.page != null && query.page > 0)
      params.set("page", String(query.page))
    if (query.page_size != null && query.page_size > 0)
      params.set("page_size", String(query.page_size))
    const qs = params.toString()

    const res = await apiRequest(
      "get",
      `${BASE}/sessions/${qs ? `?${qs}` : ""}`,
      undefined,
      { skipCache: true },
    )
    const body = res?.data?.result ?? res?.data ?? {}
    const { results, count } = extractList(body)
    return {
      data: {
        success: true,
        result: { count, results: results.map(normalizeSession) },
      },
    }
  },

  /** GET /api/chatbot/sessions/<id>/ → { conversation, messages } for the modal. */
  getConversationDetail: async (id) => {
    const sessionId = String(id ?? "").trim()
    if (!sessionId) throw new Error("Missing session id")
    const res = await apiRequest("get", `${BASE}/sessions/${sessionId}/`)
    const body = res?.data?.result ?? res?.data ?? {}
    const rawSession = pick(body, "session", "conversation") || body
    const rawMessages =
      pick(body, "messages", "history") ||
      pick(rawSession, "messages", "history") ||
      []
    return {
      data: {
        success: true,
        result: {
          conversation: normalizeSession(rawSession),
          messages: (Array.isArray(rawMessages) ? rawMessages : []).map(
            normalizeMessage,
          ),
        },
      },
    }
  },

  /** PATCH /api/chatbot/sessions/<id>/ { quality } — admin flags session quality. */
  setConversationQuality: async (id, rating) => {
    const sessionId = String(id ?? "").trim()
    if (!sessionId) throw new Error("Missing session id")
    return await apiRequest("patch", `${BASE}/sessions/${sessionId}/`, {
      quality: rating,
    })
  },

  /** DELETE /api/chatbot/sessions/<id>/ — admin deletes a session + its messages. */
  deleteConversation: async (id) => {
    const sessionId = String(id ?? "").trim()
    if (!sessionId) throw new Error("Missing session id")
    return await apiRequest("delete", `${BASE}/sessions/${sessionId}/`)
  },

  /**
   * PATCH /api/chatbot/messages/<id>/flag/ { flagged } — flag a single response.
   * Endpoint is live; the dashboard has no per-message flag control yet, so this
   * is here for when that UI is added.
   */
  flagMessage: async (messageId, flagged = true) => {
    const mId = String(messageId ?? "").trim()
    if (!mId) throw new Error("Missing message id")
    return await apiRequest("patch", `${BASE}/messages/${mId}/flag/`, {
      flagged,
    })
  },
}
